import { EditOutlined, FileUploadOutlined } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import { grey } from "@mui/material/colors";
import { ModuleRegistry } from "ag-grid-community";
import {
  RowGroupingModule,
  RowGroupingPanelModule,
  TreeDataModule,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { isEmpty, isFunction, isNil } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useColumnTypes } from "../../_src_shared/utils/agGrid/columnTypes/hooks/useColumnTypes";
import { sidebarOpen } from "../../redux/reducers/sidebarReducer";
import { formatDateShort } from "../../utils/dates/formatDateShort";
import AgGridTableWrapper from "../agGrid/AgGridTableWrapper";
import CellRendererLoanFileStatus from "../agGrid/cellRenderer/CellRendererLoanFileStatus";
import FilePreview from "../common/FilePreview";
import FilePreviewImageTypeIcon from "../common/FilePreviewImageTypeIcon";
import Flx from "../layout/Flx";
import SideCardModal from "../modals/SideCardModal";
import { DASHBOARD_SIDEBAR_TYPES } from "../sidebars/DashboardSidebar/DashboardSidebar";
import Txt from "../typography/Txt";
import { useBorrowerPortalHook } from "../../hooks/useBorrowerPortalHook";
import { profileSet } from "../../redux/actions/profileActions";
import BasicModal from "../modals/BasicModal";
import RffFormChanges from "../finalForm/RffFormChanges";
import RffDocumentMetaData from "../finalForm/features/RffDocumentMetaData";

ModuleRegistry.registerModules([
  RowGroupingPanelModule,
  RowGroupingModule,
  TreeDataModule,
]);

const BasicFilesTable = ({
  files,
  docGroup,
  docType,
  height = "100%",
  emptyContent,
  emptyMessage,
  simpleTable = true,
  gridOptions,
  headerHeight = 0,
  quickFilterText,
  showFileOptions = false,
  showUploadDate = false,
  rowGroup = null,
  hideFileCellDate = false,
  showStatus = false,
  rowStyles,
  hideDocType = false,
  defaultView = false,
  hideDocGroup = false,
  getRowStyle,
  sx,
}) => {
  const [file, setFile] = useState(null);
  const [editFile, setEditFile] = useState(null);
  const dispatch = useDispatch();
  const columnDefs = useMemo(
    () => [
      {
        headerName: "File Name",
        field: "file_display_name",
        flex: 1,
        cellStyle: {
          paddingLeft: "12px",
          alignItems: "center",
          display: "flex",
        },
        tooltipValueGetter: (p) =>
          p?.data?.isMissing
            ? "Missing Document - Click to Upload"
            : "Preview File",
        minWidth: 200,
        cellRenderer: (params) => {
          if (defaultView) {
            return params.data?.file_display_name;
          }
          return (
            <Flx
              ac
              gap={1}
              sx={{
                minWidth: 0,
                width: "100%",
                svg: {
                  mt: "3px",
                },
              }}
            >
              <FilePreviewImageTypeIcon
                size={21}
                contentType={params.data?.content_type}
              />
              <Flx column jc sx={{ minWidth: 0, flex: 1 }}>
                <Txt
                  sx={{
                    fontWeight: params?.data?.isMissing ? 400 : 500,
                    fontSize: "11px",
                    lineHeight: "18px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {params.data?.file_display_name}
                </Txt>
                {hideFileCellDate ? null : (
                  <Txt secondary sx={{ fontSize: "10px", lineHeight: 1 }}>
                    {formatDateShort(params.data?.uploadDate)}
                  </Txt>
                )}
              </Flx>
            </Flx>
          );
        },
      },
      {
        headerName: "Status",
        rowGroup: rowGroup === "documentStatus",
        field: "documentStatus",
        valueGetter: (params) => {
          const value = params.data?.documentStatus;
          return value === undefined || value === null || value === ""
            ? "Uncategorized"
            : value;
        },
        minWidth: 160,
        width: 160,
        flex: 0,
        hide: rowGroup === "documentStatus" ? true : !showStatus,
        cellClass: "ag-cell-vertical-center",
        cellStyle: {
          alignItems: "center",
          display: "flex",
        },
        autoHeight: true,
        cellRenderer: (params) => (
          <CellRendererLoanFileStatus params={params} />
        ),
      },
      {
        headerName: "Category",
        valueGetter: (params) => {
          const value = params.data?.docGroup;
          return value === undefined || value === null || value === ""
            ? "Uncategorized"
            : value;
        },
        rowGroup: rowGroup === "docGroup",
        sort: rowGroup === "docGroup" ? "asc" : null,
        field: "docGroup",
        hide:
          rowGroup === "docGroup"
            ? true
            : isNil(docGroup) && !hideDocGroup
              ? false
              : true,
        comparator: (valueA, valueB) => {
          if (valueA === "Uncategorized" && valueB !== "Uncategorized")
            return 1;
          if (valueA !== "Uncategorized" && valueB === "Uncategorized")
            return -1;
          return valueA.localeCompare(valueB);
        },
      },
      {
        headerName: "Type",
        valueGetter: (params) => {
          const value = params.data?.docType;
          return value === undefined || value === null || value === ""
            ? "Uncategorized"
            : value;
        },
        rowGroup: rowGroup === "docType",
        minWidth: 180,
        flex: 1,
        sort: rowGroup === "docType" ? "asc" : null,
        field: "docType",
        hide:
          rowGroup === "docType"
            ? true
            : isNil(docType) && !hideDocType
              ? false
              : true,
        comparator: (valueA, valueB) => {
          if (valueA === "Uncategorized" && valueB !== "Uncategorized")
            return 1;
          if (valueA !== "Uncategorized" && valueB === "Uncategorized")
            return -1;
          return valueA.localeCompare(valueB);
        },
      },
      {
        headerName: "Upload Date",
        sort: "desc",
        field: "uploadDate",
        valueFormatter: (params) => {
          return formatDateShort(params.value);
        },
        flex: 0,
        hide: !showUploadDate,
      },
      {
        width: 38,
        cellRenderer: CellRendererFileOptions,
        hide: !showFileOptions,
        field: "actions",
        headerName: "",
        pinned: "right",
        cellStyle: {
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          display: "flex",
          cursor: "pointer",
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
    ],
    [
      docGroup,
      docType,
      showFileOptions,
      showStatus,
      hideDocGroup,
      hideDocType,
      hideFileCellDate,
      showUploadDate,
      defaultView,
      rowGroup,
    ],
  );
  const columnTypes = useColumnTypes();

  const getRowId = useCallback((params) => {
    return params.data._id;
  }, []);

  const defaultColDef = useMemo(() => {
    return {
      cellClass: "ag-cell-vertical-center",
    };
  }, []);

  const onCellClicked = useCallback(
    (params) => {
      if (params?.colDef?.field === "actions") {
        console.log("params.data", params.data);
        setEditFile(params.data);
        return;
      }
      if (params?.data?.isMissing) {
        dispatch(
          sidebarOpen(DASHBOARD_SIDEBAR_TYPES.ADD_DOCUMENT, params.data, {
            ...params?.data,
            title: `Upload ${params.data.docType || "Document"}`,
          }),
        );
        return;
      }
      setFile(params.data);
    },
    [dispatch, setEditFile],
  );

  const onRowClicked = useCallback((params) => {
    const clickedElement = params.event?.target;
    // console.log("params.event?.target", params);
    if (
      (clickedElement instanceof Element && clickedElement.closest("button")) ||
      (clickedElement instanceof Element && clickedElement.closest("a"))
    ) {
      return;
    }
    if (params.node.group) {
      params.node.setExpanded(!params.node.expanded);
      return;
    }
  }, []);

  const styles = useMemo(() => {
    return {
      position: "relative",
      flexGrow: 1,
      p: 0,
      overflow: "hidden",
      ...sx,
    };
  }, [sx]);

  const getRowStyles = useCallback(
    (params) => {
      if (isFunction(getRowStyle)) {
        return getRowStyle(params);
      }
      const renderedRowStyles = isFunction(rowStyles) ? rowStyles(params) : {};
      if (params.node.group) {
        return {
          fontWeight: "600",
          backgroundColor: grey[50],
          cursor: "pointer",
          borderBottom: `1px solid ${grey[200]}`,
          // ...renderedRowStyles,
        };
      }
      return { ...renderedRowStyles };
    },
    [getRowStyle, rowStyles],
  );

  if ((emptyMessage || emptyContent) && isEmpty(files)) {
    if (emptyContent) {
      return emptyContent;
    }
    return (
      <Txt secondary sx={{ p: 1 }}>
        {emptyMessage}
      </Txt>
    );
  }

  return (
    <>
      <AgGridTableWrapper
        height={height}
        suppressMinHeightWhenPopulated={files}
        simpleTable={simpleTable}
        sx={styles}
      >
        <AgGridReact
          rowData={files}
          columnDefs={columnDefs}
          // onFirstDataRendered={onFirstDataRendered}
          defaultColDef={defaultColDef}
          quickFilterText={quickFilterText}
          onCellClicked={onCellClicked}
          onRowClicked={onRowClicked}
          headerHeight={headerHeight}
          columnTypes={columnTypes}
          getRowId={getRowId}
          getRowStyle={getRowStyles}
          groupDisplayType={"groupRows"}
          groupDefaultExpanded={1}
          domLayout="autoHeight"
          tooltipShowDelay={100}
          enableCellTextSelection
          tooltipMouseTrack
          gridOptions={gridOptions}
        />
      </AgGridTableWrapper>

      <SideCardModal
        title={file?.file_display_name || "File Preview"}
        show={!isNil(file)}
        onClose={() => setFile(null)}
        suppressBodyPadding
      >
        <FilePreview
          fileObject={file}
          containerId={file?.containerName}
          sx={{ overflowY: "auto" }}
        />
      </SideCardModal>
      <EditFileModal
        show={editFile}
        onClose={() => setEditFile(null)}
        file={editFile}
      />
    </>
  );
};

const CellRendererFileOptions = (params) => {
  const file = useMemo(() => params?.data, [params?.data]);

  let content = null;
  if (params?.data?.isMissing) {
    content = (
      <Tooltip title={`Upload ${file.docType}`}>
        <FileUploadOutlined className="thin" />
      </Tooltip>
    );
  } else {
    content = (
      <Tooltip title="Edit File">
        <EditOutlined className="thin" />
        {/* <IconButton
          onClick={() =>
            dispatch(
              sidebarOpen(
                DASHBOARD_SIDEBAR_TYPES.EDIT_BORROWER_DOCUMENT_METADATA,
                file,
              ),
            )
          }
          size="small"
        >
          <EditOutlined className="thin" />
        </IconButton> */}
      </Tooltip>
    );
  }

  return (
    <Flx sx={{ height: "100%" }} center>
      {content}
    </Flx>
  );
};

const EditFileModal = ({ show, onClose, file }) => {
  console.log("EditFileModal", file);
  const { loading, updateBorrowerDocMetaData } = useBorrowerPortalHook();
  const dispatch = useDispatch();
  const onSubmit = (changedValues, { form, values, changes }) => {
    console.log("File changes submitted", {
      changedValues,
      form,
      values,
      changes,
    });

    const payload = [{ _id: file._id, ...changedValues }];

    updateBorrowerDocMetaData({
      data: payload,
      onSuccessFn: (res) => {
        dispatch(profileSet(res));
        onClose();
      },
    });
  };

  const initialValues = useMemo(() => {
    return {
      file_display_name: file?.file_display_name,
      docGroup: file?.docGroup,
      docType: file?.docType,
    };
  }, [file]);
  return (
    <BasicModal
      maxWidth="sm"
      title={`Edit ${file?.file_display_name}`}
      show={show}
      onClose={onClose}
    >
      <RffFormChanges
        loading={loading}
        initialValues={initialValues}
        onSubmit={onSubmit}
      >
        <RffDocumentMetaData fileNameAsLoaded={file?.file_name_as_loaded} />
      </RffFormChanges>
    </BasicModal>
  );
};
export default BasicFilesTable;
